import { useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { NewOnboardingDialog } from '../components/onboarding/NewOnboardingDialog.js';
import { useApi } from '../lib/api.js';
import s from './OnboardingQueuePage.module.scss';

// Permissive parse: rate_to_client present for an owner, omitted otherwise (T-186).
const QueueItem = z.object({
  id: z.string().uuid(),
  state: z.string(),
  admin_resource_name: z.string(),
  admin_business_email: z.string(),
  // Existing vendor's name for a resource-only onboarding; null until a new
  // vendor is created at submit.
  vendor_name: z.string().nullable(),
  rate_to_contractor: z.number(),
  rate_to_client: z.number().nullable().optional(),
  client: z.object({
    id: z.string().uuid(),
    name: z.string(),
    project_or_product_name: z.string(),
  }),
  start_date: z.string(),
  end_date: z.string(),
  product_service: z.string(),
  magic_link_expires_at: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
type QueueItem = z.infer<typeof QueueItem>;
const QueueList = z.array(QueueItem);

const STATE_LABELS: Record<string, string> = {
  pending_contractor_input: 'Waiting on vendor',
  pending_contract_signature: 'Waiting on vendor signature',
  pending_admin_signature: 'Waiting on admin signature',
  active: 'Active',
  expired: 'Expired',
  cancelled: 'Cancelled',
  draft: 'Draft',
};

function stateLabel(state: string): string {
  return STATE_LABELS[state] ?? state;
}

function stateBadgeClass(state: string): string | undefined {
  if (state === 'cancelled' || state === 'expired') return s.badgeMuted;
  if (state === 'active') return s.badgeSuccess;
  return s.badgePending;
}

function ageLabel(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function OnboardingQueuePage() {
  const [filterAll, setFilterAll] = useState(false);
  const url = filterAll ? '/api/onboardings?state=cancelled' : '/api/onboardings';
  const { data, error, loading, refetch } = useApi<QueueItem[]>(url, QueueList);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1 className={s.title}>Onboarding queue</h1>
          <p className={s.subtitle}>
            In-flight onboardings. Click a row to see details, resend the magic link, or cancel.
          </p>
        </div>
        <button
          type="button"
          className={s.newButton}
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          + New onboarding
        </button>
      </div>

      <div className={s.filters}>
        <button
          type="button"
          className={!filterAll ? s.filterActive : s.filter}
          onClick={() => {
            setFilterAll(false);
          }}
        >
          In flight
        </button>
        <button
          type="button"
          className={filterAll ? s.filterActive : s.filter}
          onClick={() => {
            setFilterAll(true);
          }}
        >
          Cancelled
        </button>
      </div>

      {error && <div className={s.error}>Failed to load onboardings: {error.message}</div>}
      {!error && loading && !data && <div className={s.loading}>Loading onboardings…</div>}

      {data && data.length === 0 && (
        <div className={s.empty}>
          {filterAll
            ? 'No cancelled onboardings.'
            : 'No onboardings in flight. Click + New onboarding to start one.'}
        </div>
      )}

      {data && data.length > 0 && (
        <table className={s.table}>
          <thead>
            <tr>
              <th scope="col">Resource</th>
              <th scope="col">Vendor</th>
              <th scope="col">Client</th>
              <th scope="col">State</th>
              <th scope="col">Started</th>
              <th scope="col">Link expires</th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className={s.resourceName}>{row.admin_resource_name}</div>
                  <div className={s.resourceEmail}>{row.admin_business_email}</div>
                </td>
                <td>{row.vendor_name ?? <span className={s.resourceEmail}>— pending —</span>}</td>
                <td>{row.client.name}</td>
                <td>
                  <span className={stateBadgeClass(row.state)}>{stateLabel(row.state)}</span>
                </td>
                <td>{ageLabel(row.created_at)}</td>
                <td>{new Date(row.magic_link_expires_at).toLocaleDateString()}</td>
                <td>
                  <div className={s.actions}>
                    <Link
                      to={`/onboarding/${row.id}`}
                      className={s.actionBtn}
                      aria-label={`View ${row.admin_resource_name}`}
                    >
                      View
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <NewOnboardingDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          void refetch();
        }}
      />
    </div>
  );
}
